import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Grid, Typography, Chip, CircularProgress } from "@material-ui/core";
import { Phone as PhoneIcon } from "@material-ui/icons";
import { apiFetchChatInsights } from "../api/index.js";
import { getUserFromLocalStorage } from "../utilities/user.js";
import ChatAnalytics from "../components/ChatAnalytics.js";
import socket from "../utilities/socket";

const resolveVendorId = (profile) =>
  profile?.result?.parentVendorId || profile?.result?._id || "";

const fmt = (n) => Number(n || 0).toLocaleString();

const fmtMins = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

// Short, human "time ago" for template rejection timestamps. Falls back to a
// compact locale date once the event is more than a few days old, and to "—"
// when the value is missing or unparseable (older backends may omit `at`).
const relTime = (at) => {
  if (!at) return "—";
  const t = new Date(at).getTime();
  if (Number.isNaN(t)) return "—";
  const diffMs = Date.now() - t;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// A pair/row of headline figures used by several panels.
const Stat = ({ value, label, danger }) => (
  <div>
    <Typography variant="h5" style={{ fontWeight: 800, fontSize: 22, color: danger ? "#b91c1c" : undefined }}>{value}</Typography>
    <Typography variant="caption" style={{ color: "#94a3b8" }}>{label}</Typography>
  </div>
);

// ── Presentational helpers for the adorned list panels ──────────────────────
const initials = (s) =>
  (String(s || "").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("") || "?").toUpperCase();

const prettyPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length < 9) return p;
  return `+${d.replace(/^(\d{3})(\d{3})(\d{3})(\d+)$/, "$1 $2 $3 $4")}`;
};

const AVATAR_COLORS = ["#0f766e", "#1d4ed8", "#7e22ce", "#b45309", "#be123c", "#0e7490", "#4d7c0f", "#0891b2"];
const colorFor = (s) =>
  AVATAR_COLORS[String(s || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];

// Rank chip: medal tones for the top 3, slate for the rest.
const rankBg = (i) => (i === 0 ? "#f59e0b" : i === 1 ? "#9ca3af" : i === 2 ? "#b45309" : "#cbd5e1");

const Pill = ({ children, tone = "slate" }) => {
  const tones = {
    slate: { background: "#eef2f6", color: "#475569" },
    blue: { background: "#e0edff", color: "#1d4ed8" },
    green: { background: "#dcfce7", color: "#15803d" },
  };
  return (
    <span style={{ ...tones[tone], fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
};

const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const StatusChipColor = {
  DELIVERED: { background: "#dcfce7", color: "#15803d" },
  IN_TRANSIT: { background: "#e0edff", color: "#1d4ed8" },
  DISPATCHED: { background: "#efe1fb", color: "#7e22ce" },
  ASSIGNED: { background: "#d6f3ee", color: "#0f766e" },
  PROCESSING: { background: "#fdf0d5", color: "#b45309" },
  PENDING: { background: "#fde0e0", color: "#b91c1c" },
  REJECTED: { background: "#fde0e0", color: "#b91c1c" },
};

const PriorityChipColor = {
  urgent: { background: "#fde0e0", color: "#b91c1c" },
  high: { background: "#fdf0d5", color: "#b45309" },
  normal: { background: "#e0edff", color: "#1d4ed8" },
  low: { background: "#eef2f6", color: "#64748b" },
};

const prettyCategory = (c) =>
  String(c || "other").replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());

const Section = ({ title, subtitle, children }) => (
  <Card style={{ borderRadius: 12, height: "100%" }}>
    <CardContent>
      <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{title}</Typography>
      {subtitle && <Typography variant="caption" style={{ color: "#94a3b8" }}>{subtitle}</Typography>}
      <div style={{ marginTop: 12 }}>{children}</div>
    </CardContent>
  </Card>
);

const ConversationAnalytics = () => {
  const profile = useMemo(() => getUserFromLocalStorage(), []);
  const vendorId = resolveVendorId(profile);
  // Superadmins consume analytics holistically across the whole ecosystem.
  const isSuperAdmin =
    Boolean(profile?.result?.isSuperAdmin) ||
    ["SUPERUSER", "SUPERADMIN", "ADMIN"].includes(
      profile?.result?.user_role || profile?.result?.role
    );
  const scope = isSuperAdmin ? "all" : "vendor";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!vendorId) return undefined;
    let active = true;
    const load = (initial) => {
      if (initial) setLoading(true);
      return apiFetchChatInsights(vendorId, scope)
        .then(({ data }) => active && setData(data.insights))
        .catch(() => active && setError("Could not load conversation insights."))
        .finally(() => active && initial && setLoading(false));
    };
    load(true);

    // Real-time refresh on chat/order activity (debounced), poll as fallback.
    let debounce = null;
    const refresh = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => load(false), 800);
    };
    socket.emit("joinVendorInbox", { vendorId });
    socket.on("inbox:message_received", refresh);
    socket.on("inbox:message_sent", refresh);
    socket.on("inbox:order_created", refresh);
    socket.on("inbox:conversation_closed", refresh);
    socket.on("inbox:conversation_assigned", refresh);
    socket.on("inbox:ticket_created", refresh);

    const interval = setInterval(() => load(false), 30000);
    return () => {
      active = false;
      clearInterval(interval);
      clearTimeout(debounce);
      socket.emit("leaveVendorInbox", { vendorId });
      socket.off("inbox:message_received", refresh);
      socket.off("inbox:message_sent", refresh);
      socket.off("inbox:order_created", refresh);
      socket.off("inbox:conversation_closed", refresh);
      socket.off("inbox:conversation_assigned", refresh);
      socket.off("inbox:ticket_created", refresh);
    };
  }, [vendorId, scope]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <CircularProgress />
      </div>
    );
  }
  if (error || !data) {
    return <div style={{ padding: 24, color: "#64748b" }}>{error || "No data."}</div>;
  }

  const s = data.summary;

  return (
    <div style={{ padding: 18 }}>
      <Typography variant="h5" style={{ fontWeight: 800, marginBottom: 4, fontSize: 22 }}>
        Conversation Analytics{isSuperAdmin ? " — Ecosystem" : ""}
      </Typography>
      <Typography variant="body2" style={{ color: "#64748b", marginBottom: 18 }}>
        {isSuperAdmin
          ? "Holistic view across every vendor — conversion, order outcomes, top WhatsApp customers, and operational bottlenecks platform-wide."
          : "How chats are influencing your workflow — conversion, order outcomes, top WhatsApp customers, and operational bottlenecks."}
      </Typography>

      {/* Top-line cards reuse the dashboard band */}
      <ChatAnalytics vendorId={vendorId} scope={scope} />

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12} md={6}>
          <Section title="End status of chat-driven orders" subtitle="Where orders placed through chat ended up">
            {data.endOrderStatus.length === 0 && <Typography variant="body2" style={{ color: "#94a3b8" }}>No chat orders yet.</Typography>}
            {data.endOrderStatus.map((o) => (
              <div key={o.status} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f1f5f4" }}>
                <Chip size="small" label={o.status} style={StatusChipColor[o.status] || { background: "#eef2f6", color: "#475569" }} />
                <strong>{fmt(o.count)}</strong>
              </div>
            ))}
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Top WhatsApp customers" subtitle="Customers who most often chat to order">
            {data.topWhatsappCustomers.length === 0 && <Typography variant="body2" style={{ color: "#94a3b8" }}>No WhatsApp customers yet.</Typography>}
            {data.topWhatsappCustomers.map((c, i, arr) => {
              const named = Boolean(c.name);
              const primary = named ? c.name : prettyPhone(c.phone);
              return (
                <div key={c.phone} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i === arr.length - 1 ? "none" : "1px solid #f1f5f4" }}>
                  <div style={{ width: 38, height: 38, flex: "0 0 38px", borderRadius: "50%", background: colorFor(primary), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>
                    {named ? initials(c.name) : <PhoneIcon style={{ fontSize: 18 }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, ...ellipsis }}>{primary}</div>
                    {named && <div style={{ color: "#94a3b8", fontSize: 12, ...ellipsis }}>{prettyPhone(c.phone)}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
                    <Pill tone="blue">{fmt(c.conversations)} chats</Pill>
                    <Pill tone={c.orders > 0 ? "green" : "slate"}>{fmt(c.orders)} orders</Pill>
                  </div>
                </div>
              );
            })}
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Unresolved tickets" subtitle="Open issues by priority and how long they've waited">
            {data.unresolvedTickets.count === 0 && <Typography variant="body2" style={{ color: "#15803d" }}>No unresolved tickets. Nice.</Typography>}
            {data.unresolvedTickets.items.map((t) => (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f1f5f4" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {t.priority && <Chip size="small" label={t.priority} style={{ height: 18, fontWeight: 700, ...(PriorityChipColor[t.priority] || PriorityChipColor.normal) }} />}
                  {prettyCategory(t.category)}
                  <Chip size="small" label={t.status} style={{ height: 18 }} />
                </span>
                <span style={{ color: t.ageHours > 24 ? "#b91c1c" : "#64748b", fontWeight: t.ageHours > 24 ? 700 : 400, fontSize: 13 }}>{t.ageHours}h</span>
              </div>
            ))}
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Tickets by category" subtitle="Recurring problem types over the last 30 days">
            {(!data.ticketsByCategory || data.ticketsByCategory.length === 0) && <Typography variant="body2" style={{ color: "#94a3b8" }}>No tickets in the last 30 days.</Typography>}
            {(data.ticketsByCategory || []).map((c) => (
              <div key={c.category} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f1f5f4" }}>
                <span>{prettyCategory(c.category)}</span>
                <strong>{fmt(c.count)}</strong>
              </div>
            ))}
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Ticket resolution" subtitle="Throughput and speed over the last 30 days">
            <div style={{ display: "flex", gap: 24 }}>
              <div>
                <Typography variant="h5" style={{ fontWeight: 800, fontSize: 24 }}>{fmt(data.ticketResolution?.resolvedLast30d)}</Typography>
                <Typography variant="caption" style={{ color: "#94a3b8" }}>Resolved</Typography>
              </div>
              <div>
                <Typography variant="h5" style={{ fontWeight: 800, fontSize: 24 }}>
                  {data.ticketResolution?.avgResolutionHours != null ? `${fmt(data.ticketResolution.avgResolutionHours)}h` : "—"}
                </Typography>
                <Typography variant="caption" style={{ color: "#94a3b8" }}>Avg time to resolve</Typography>
              </div>
            </div>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Stale conversations" subtitle="Open chats with no recent activity — possible bottlenecks">
            {data.staleConversations.count === 0 && <Typography variant="body2" style={{ color: "#15803d" }}>Nothing stale.</Typography>}
            {data.staleConversations.items.map((c) => (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f1f5f4" }}>
                <span>{c.name || c.phone} <span style={{ color: "#94a3b8", fontSize: 12 }}>· {c.status}</span></span>
                <span style={{ color: c.waitingHours > 24 ? "#b91c1c" : "#64748b", fontWeight: c.waitingHours > 24 ? 700 : 400, fontSize: 13 }}>{c.waitingHours}h idle</span>
              </div>
            ))}
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Response time" subtitle="How fast the first reply reaches a customer">
            <div style={{ display: "flex", gap: 24 }}>
              <Stat value={fmtMins(data.responseTime?.avgFirstResponseMinutes)} label="Avg first response" />
              <Stat value={fmtMins(data.responseTime?.medianFirstResponseMinutes)} label="Median first response" />
            </div>
            <Typography variant="caption" style={{ color: "#94a3b8" }}>
              Across {fmt(data.responseTime?.conversationsMeasured)} answered conversations
            </Typography>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="24-hour window health" subtitle="WhatsApp only allows free replies within 24h of the customer's last message">
            <div style={{ display: "flex", gap: 24 }}>
              <Stat value={fmt(data.windowHealth?.missedWindows)} label="Windows closed unanswered" danger={data.windowHealth?.missedWindows > 0} />
              <Stat value={fmt(data.windowHealth?.templatesSent)} label="Templates sent (30d)" />
            </div>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Template health" subtitle="WhatsApp message templates submitted to Meta">
            {(() => {
              const th = data.templateHealth;
              // Older backends won't send templateHealth — show a calm empty state
              // rather than rendering NaNs or crashing on undefined.
              if (!th) {
                return <Typography variant="body2" style={{ color: "#94a3b8" }}>No template data yet.</Typography>;
              }
              const rejections = Array.isArray(th.recentRejections) ? th.recentRejections : [];
              return (
                <>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                    <Stat value={fmt(th.approved)} label="Approved" />
                    <Stat value={fmt(th.pending)} label="Pending" />
                    <Stat value={fmt(th.rejected)} label="Rejected" danger={Number(th.rejected) > 0} />
                    <Stat value={fmt(th.total)} label="Total" />
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    <Pill tone="green">
                      {th.approvalRatePct != null ? `${fmt(th.approvalRatePct)}%` : "—"} approval rate
                    </Pill>
                    <Pill tone="blue">
                      {th.avgApprovalHours != null ? `${fmt(th.avgApprovalHours)}h` : "—"} avg approval time
                    </Pill>
                    {Number(th.paused) > 0 && <Pill tone="slate">{fmt(th.paused)} paused</Pill>}
                    {Number(th.disabled) > 0 && <Pill tone="slate">{fmt(th.disabled)} disabled</Pill>}
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <Typography variant="caption" style={{ color: "#94a3b8" }}>Recent rejections</Typography>
                    {rejections.length === 0 ? (
                      <Typography variant="body2" style={{ color: "#15803d", marginTop: 4 }}>None lately. Nice.</Typography>
                    ) : (
                      rejections.map((r, i) => (
                        <div key={`${r.name}-${r.language}-${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "8px 0", borderBottom: i === rejections.length - 1 ? "none" : "1px solid #f1f5f4" }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, ...ellipsis }} title={r.name}>
                              {r.name}{r.language ? <span style={{ color: "#94a3b8", fontWeight: 400 }}> · {r.language}</span> : null}
                            </div>
                            {r.reason && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 2 }}>{r.reason}</div>}
                          </div>
                          <span style={{ color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap", flex: "0 0 auto" }}>{relTime(r.at)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </>
              );
            })()}
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Payment → fulfilment funnel" subtitle="Where chat-driven orders are in the money flow">
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <Stat value={fmt(data.paymentFunnel?.created)} label="Created" />
              <Stat value={fmt(data.paymentFunnel?.paid)} label="Payment confirmed" />
              <Stat value={fmt(data.paymentFunnel?.dispatched)} label="Dispatched" />
              <Stat value={fmt(data.paymentFunnel?.delivered)} label="Delivered" />
            </div>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Customer mix & order value" subtitle="Loyalty and basket size from chat">
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <Stat value={fmt(data.customerMix?.new)} label="New customers" />
              <Stat value={fmt(data.customerMix?.returning)} label="Returning" />
              <Stat value={`${fmt(data.customerMix?.returningRate)}%`} label="Returning rate" />
              <Stat value={`UGX ${fmt(data.customerMix?.avgOrderValue)}`} label="Avg order value" />
            </div>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Agent performance" subtitle="Conversations handled and converted per agent">
            {(!data.agentPerformance || data.agentPerformance.length === 0) && <Typography variant="body2" style={{ color: "#94a3b8" }}>No assigned conversations yet.</Typography>}
            {(data.agentPerformance || []).map((a) => (
              <div key={a.agentId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f1f5f4" }}>
                <span>{a.name}</span>
                <span style={{ color: "#64748b", fontSize: 13 }}>{fmt(a.conversations)} chats · {fmt(a.orders)} orders · {a.conversionRate}%</span>
              </div>
            ))}
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Product demand" subtitle="Most-ordered items through chat">
            {(!data.productDemand || data.productDemand.length === 0) && <Typography variant="body2" style={{ color: "#94a3b8" }}>No chat orders yet.</Typography>}
            {(() => {
              const items = data.productDemand || [];
              const maxQty = Math.max(1, ...items.map((p) => p.qty));
              return items.map((p, i) => (
                <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i === items.length - 1 ? "none" : "1px solid #f1f5f4" }}>
                  <div style={{ width: 24, height: 24, flex: "0 0 24px", borderRadius: 8, background: rankBg(i), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={ellipsis} title={p.name}>{p.name}</div>
                    <div style={{ height: 6, background: "#eef2f6", borderRadius: 999, marginTop: 6, overflow: "hidden" }}>
                      <div style={{ width: `${Math.max(6, (p.qty / maxQty) * 100)}%`, height: "100%", background: "#128c7e", borderRadius: 999 }} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flex: "0 0 auto" }}>
                    <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1 }}>{fmt(p.qty)}</div>
                    <div style={{ color: "#94a3b8", fontSize: 11 }}>{fmt(p.orders)} orders</div>
                  </div>
                </div>
              ));
            })()}
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title="Busiest hours" subtitle="When customers message most (East Africa Time) — staff accordingly">
            {(!data.busiestHours || data.busiestHours.length === 0) ? (
              <Typography variant="body2" style={{ color: "#94a3b8" }}>No inbound messages yet.</Typography>
            ) : (
              (() => {
                const byHour = Object.fromEntries((data.busiestHours || []).map((h) => [h.hour, h.count]));
                const maxH = Math.max(1, ...Object.values(byHour));
                return (
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 90 }}>
                    {Array.from({ length: 24 }, (_, h) => {
                      const count = byHour[h] || 0;
                      return (
                        <div key={h} style={{ flex: 1, textAlign: "center" }} title={`${count} at ${hourLabel(h)}`}>
                          <div style={{ height: `${(count / maxH) * 64}px`, minHeight: 2, background: count ? "#128c7e" : "#e5e9ee", borderRadius: "4px 4px 0 0" }} />
                          {h % 3 === 0 && <Typography variant="caption" style={{ color: "#94a3b8", fontSize: 10 }}>{h}</Typography>}
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title="WhatsApp spend (approximate)" subtitle="Billable conversations are a proxy — confirm exact cost in Meta WhatsApp Manager">
            <Typography variant="h5" style={{ fontWeight: 800, fontSize: 24 }}>{fmt(data.billableConversationsApprox)}</Typography>
            <Typography variant="body2" style={{ color: "#64748b" }}>
              WhatsApp conversations started in the last 30 days. Meta bills per 24-hour conversation window; use this as a directional indicator and reconcile with WhatsApp Manager billing for exact figures.
            </Typography>
          </Section>
        </Grid>
      </Grid>
    </div>
  );
};

export default ConversationAnalytics;
